import { createContext, useContext, useId, useState } from "react";
import { Link } from "react-router";

import {
  classifyRunReadFailure,
  usePreviewRunEventQuery,
} from "@/api/previewInsights";
import type { PreviewSimRunStatus } from "@/api/previewSim";
import { ANXIETY_UNIT_LABELS, anxietyUnitOf } from "@/lib/anxietyView";
import {
  labSimStatForTurn,
  type LabTurn,
  type LabTurnDiff,
} from "@/lib/labModel";
import {
  RATIONALE_STATE_LABELS,
  describeCriterionResult,
  describeDisposition,
  describeOperator,
  describePosition,
  describeRoutingReason,
  describeSafetyDetection,
  describeSafetyIntervention,
  describeSafetyMechanism,
  describeSafetyReason,
  describeWhyNotReason,
  describeWithheld,
  formatScalar,
  positionsDiffer,
  previewRunTabPath,
  readContentSlot,
  readInlineBody,
  safetyOutcomeLabel,
  safetyOutcomeTone,
  type ClassificationEvent,
  type CriterionEvent,
  type ContentSlot,
} from "@/lib/previewDecisions";
import {
  EVENT_STATUS_LABELS,
  EVENT_STATUS_TONES,
  readPromptCall,
} from "@/lib/previewPromptCall";
import { PromptCallCard } from "@/routes/PreviewRunPromptCall";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Tooltip } from "@/ui/Tooltip";

import {
  labTurnForRef,
  resolveLabRef,
  type LabRunModel,
} from "@/lib/labRunModel";

// ── Sections ────────────────────────────────────────────────────────────────

/** The id of the side label ("Run A") a section is under, so equal headings stay distinguishable. */
export const SideLabelId = createContext<string | null>(null);

export function Section({
  title,
  help,
  differs = false,
  children,
}: {
  title: string;
  /** One plain sentence, shown when the title is hovered or focused. */
  help?: string;
  differs?: boolean;
  children: React.ReactNode;
}) {
  const id = useId();
  const side = useContext(SideLabelId);
  return (
    <section
      aria-labelledby={side === null ? id : `${side} ${id}`}
      className="space-y-2 rounded-md border border-border bg-card p-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        {help === undefined ? (
          <h4 id={id} className="text-sm font-semibold">
            {title}
          </h4>
        ) : (
          <Tooltip content={help}>
            <h4
              id={id}
              // Focusable so keyboard users get the explanation too.
              // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
              tabIndex={0}
              className="text-sm font-semibold"
            >
              {title}
            </h4>
          </Tooltip>
        )}
        {differs ? <Badge tone="warning">Differs</Badge> : null}
      </div>
      {children}
    </section>
  );
}

export const NOTHING = (
  <p className="text-sm text-muted-foreground">Nothing recorded at this turn</p>
);

export function LabTurnSummary({
  projectId,
  runRef,
  turn,
  sim,
  diff = null,
  sideLabel = null,
}: {
  projectId: string;
  runRef: string;
  /** "Run A" / "Run B" in compare mode: names the sections for assistive tech. */
  sideLabel?: string | null;
  turn: LabTurn;
  sim: PreviewSimRunStatus | null;
  /** Set in compare mode when both runs have this turn. */
  diff?: LabTurnDiff | null;
}) {
  const [openCall, setOpenCall] = useState<string | null>(null);
  const stat = labSimStatForTurn(sim, turn.turnNumber);
  const orphanWhyNots = turn.whyNots.filter(
    (w) => !turn.routings.some((r) => r.eventRef === w.detail.decisionEventRef),
  );
  const hasPosition = turn.before !== null && turn.after !== null;

  const sideId = useId();
  return (
    <SideLabelId.Provider value={sideLabel === null ? null : sideId}>
      <div className="space-y-3">
        {sideLabel !== null ? (
          <span id={sideId} className="sr-only">
            {sideLabel}
          </span>
        ) : null}
        <Section
          title="Classification"
          help="What each classification decided on this turn, with its reasoning."
          differs={diff?.classification === true}
        >
          {turn.classifications.length === 0
            ? NOTHING
            : turn.classifications.map((event) => (
                <ClassificationItem
                  key={event.eventRef}
                  projectId={projectId}
                  runRef={runRef}
                  event={event}
                />
              ))}
        </Section>

        {turn.criteria.length > 0 || diff?.threshold === true ? (
          <Section
            title="Thresholds"
            help="The pass marks checked on this turn and whether each passed."
            differs={diff?.threshold === true}
          >
            {turn.criteria.length === 0 ? (
              NOTHING
            ) : (
              <CriteriaTable criteria={turn.criteria} />
            )}
          </Section>
        ) : null}

        <Section
          title="Routing / why-not"
          help="Which path Liv chose and why the other options were not taken."
          differs={diff?.routing === true}
        >
          {turn.routings.length === 0 && orphanWhyNots.length === 0
            ? NOTHING
            : null}
          {turn.routings.map((routing) => {
            const alternatives = turn.whyNots.filter(
              (w) => w.detail.decisionEventRef === routing.eventRef,
            );
            const { detail } = routing;
            return (
              <div key={routing.eventRef} className="space-y-1 text-sm">
                <p>
                  Selected{" "}
                  <code dir="ltr" className="font-mono text-xs">
                    {detail.selectedChoice}
                  </code>{" "}
                  for{" "}
                  <code dir="ltr" className="font-mono text-xs">
                    {detail.decisionId}
                  </code>
                </p>
                <p className="text-muted-foreground">
                  Considered:{" "}
                  <span dir="ltr">
                    {detail.consideredChoices.join(", ") || "none listed"}
                  </span>
                </p>
                <p>{describeRoutingReason(detail.reasonCode)}</p>
                {alternatives.length > 0 ? (
                  <AlternativeList items={alternatives} />
                ) : null}
              </div>
            );
          })}
          {orphanWhyNots.length > 0 ? (
            <div className="space-y-1 text-sm">
              <p className="text-xs font-medium text-muted-foreground">
                Other alternatives
              </p>
              <AlternativeList items={orphanWhyNots} />
            </div>
          ) : null}
        </Section>

        <Section
          title="Flow position"
          help="Where the conversation was in the flow before this turn and where it moved."
          differs={diff?.transition === true}
        >
          {!hasPosition && turn.transitions.length === 0 ? NOTHING : null}
          {turn.before !== null && turn.after !== null ? (
            positionsDiffer(turn.before, turn.after) ? (
              <p className="text-sm">
                <span dir="auto">{describePosition(turn.before)}</span> →{" "}
                <span dir="auto">{describePosition(turn.after)}</span>
              </p>
            ) : (
              <p className="text-sm">
                Position unchanged:{" "}
                <span dir="auto">{describePosition(turn.after)}</span>
              </p>
            )
          ) : null}
          {turn.transitions.map((event) => (
            <p key={event.eventRef} className="text-sm">
              Transition:{" "}
              <span dir="auto">{describePosition(event.detail.from)}</span> →{" "}
              <span dir="auto">{describePosition(event.detail.to)}</span>
              {event.detail.reasonCode !== null ? (
                <span className="text-muted-foreground">
                  {" "}
                  (
                  <code dir="ltr" className="font-mono text-xs">
                    {event.detail.reasonCode}
                  </code>
                  )
                </span>
              ) : null}
            </p>
          ))}
        </Section>

        {turn.promptCalls.length > 0 ? (
          <Section
            title="Prompt calls"
            help="The prompts Liv ran on this turn, with the model used and the outcome."
          >
            <ul className="space-y-2">
              {turn.promptCalls.map((record) => {
                const reading = readPromptCall(record.record);
                if (reading.kind === "unreadable") {
                  return (
                    <li
                      key={record.eventRef}
                      className="text-sm text-muted-foreground"
                    >
                      This call could not be read by this build
                    </li>
                  );
                }
                const { call } = reading;
                const name = call.label ?? "Prompt call";
                const open = openCall === call.eventRef;
                return (
                  <li key={record.eventRef} className="space-y-2 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <span dir="auto" className="font-medium">
                        {name}
                      </span>
                      {call.callType !== null ? (
                        <code
                          dir="ltr"
                          className="font-mono text-xs text-muted-foreground"
                        >
                          {call.callType}
                        </code>
                      ) : null}
                      <Badge tone={EVENT_STATUS_TONES[call.status]}>
                        {EVENT_STATUS_LABELS[call.status]}
                      </Badge>
                      {call.modelIdentity?.model ? (
                        <span
                          dir="ltr"
                          className="text-xs text-muted-foreground"
                        >
                          {call.modelIdentity.model}
                        </span>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        aria-expanded={open}
                        aria-label={`${open ? "Hide" : "Open"} prompt call: ${name}`}
                        onClick={() => setOpenCall(open ? null : call.eventRef)}
                      >
                        {open ? "Hide" : "Open"}
                      </Button>
                      <Link
                        className="text-sm underline underline-offset-2"
                        aria-label={`Open in Prompt Inspector: ${name}`}
                        to={previewRunTabPath(
                          projectId,
                          runRef,
                          "prompts",
                          call.eventRef,
                        )}
                      >
                        Open in Prompt Inspector
                      </Link>
                    </div>
                    {open ? (
                      <PromptCallCard
                        call={call}
                        projectId={projectId}
                        previewRunRef={runRef}
                        title="Prompt call"
                      />
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </Section>
        ) : null}

        {turn.safety.length > 0 || diff?.safety === true ? (
          <Section
            title="Safety"
            help="The safety checks on this turn, what each found and its effect on the reply."
            differs={diff?.safety === true}
          >
            {turn.safety.length === 0 ? NOTHING : null}
            {turn.safety.map((event) => {
              const { detail } = event;
              return (
                <div key={event.eventRef} className="space-y-1 text-sm">
                  <p className="flex flex-wrap items-center gap-2">
                    <span dir="auto" className="font-medium">
                      {describeSafetyMechanism(detail.mechanism) ??
                        detail.mechanism}
                    </span>
                    <Badge tone={safetyOutcomeTone(detail.outcome)}>
                      {safetyOutcomeLabel(detail.outcome)}
                    </Badge>
                  </p>
                  <p>{describeSafetyReason(detail.reasonCode)}</p>
                  <p className="text-muted-foreground">
                    {describeSafetyDetection(detail.detection)}{" "}
                    {describeSafetyIntervention(detail.intervention)}
                  </p>
                  <p dir="auto" className="whitespace-pre-wrap break-words">
                    {detail.clinicalEffect}
                  </p>
                </div>
              );
            })}
          </Section>
        ) : null}

        {sim !== null ? (
          <Section
            title="Sim stats"
            help="Reply time, model name and amount of text processed for the simulated patient."
          >
            {stat === null ? (
              <p className="text-sm text-muted-foreground">
                The simulator reported no stats for this turn.
              </p>
            ) : (
              <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                <Stat label="Latency">
                  <span dir="ltr">{stat.latencyMs} ms</span>
                </Stat>
                <Stat label="Model">
                  <span dir="ltr">{stat.model}</span>
                </Stat>
                <Stat label="Prompt tokens">
                  <span dir="ltr">{stat.promptTokens ?? "Not reported"}</span>
                </Stat>
                <Stat label="Completion tokens">
                  <span dir="ltr">
                    {stat.completionTokens ?? "Not reported"}
                  </span>
                </Stat>
              </dl>
            )}
          </Section>
        ) : null}
      </div>
    </SideLabelId.Provider>
  );
}

function Stat({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd>{children}</dd>
    </div>
  );
}

export function AlternativeList({ items }: { items: LabTurn["whyNots"] }) {
  return (
    <ul className="space-y-1">
      {items.map((w) => (
        <li key={w.eventRef} className="text-sm">
          <code dir="ltr" className="font-mono text-xs">
            {w.detail.alternative}
          </code>
          : {describeDisposition(w.detail.disposition)}.{" "}
          {describeWhyNotReason(w.detail.reasonCode)}
          {w.detail.unmetCriteria.length > 0 ? (
            <span className="block text-muted-foreground">
              Unmet criteria:{" "}
              <span dir="ltr">{w.detail.unmetCriteria.join(", ")}</span>
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

// ── Classification (body slots on the user's say-so) ────────────────────────

export function ClassificationItem({
  projectId,
  runRef,
  event,
  scoreState = false,
  children,
}: {
  projectId: string;
  runRef: string;
  event: ClassificationEvent;
  /** The Classification tab states an absent score ("none recorded") instead of omitting it. */
  scoreState?: boolean;
  /** Rendered after the bodies (the tab's resolved input references). */
  children?: React.ReactNode;
}) {
  const { detail } = event;
  const unit = anxietyUnitOf(detail.classifierId);
  const rationale =
    detail.rationaleState === "recorded" ? detail.rationale : null;
  const needsRead =
    detail.result.state === "reference" || rationale?.state === "reference";
  const [requested, setRequested] = useState(false);
  const read = usePreviewRunEventQuery(projectId, runRef, event.eventRef, {
    enabled: requested && needsRead,
  });
  const fetchedDetail = (
    read.data?.event as Record<string, unknown> | undefined
  )?.["detail"];
  const fetched = (name: "result" | "rationale"): ContentSlot | null =>
    typeof fetchedDetail === "object" &&
    fetchedDetail !== null &&
    !Array.isArray(fetchedDetail)
      ? readContentSlot((fetchedDetail as Record<string, unknown>)[name])
      : null;
  const slotOf = (
    name: "result" | "rationale",
    compact: ContentSlot,
  ): ContentSlot | null =>
    compact.state === "reference" ? fetched(name) : compact;

  return (
    <div className="space-y-2 border-b border-border pb-2 text-sm last:border-b-0 last:pb-0">
      <p className="flex flex-wrap items-center gap-2">
        <code dir="ltr" className="font-mono text-xs">
          {detail.classifierId}
        </code>
        {unit !== null ? (
          <Badge tone="outline">{ANXIETY_UNIT_LABELS[unit]}</Badge>
        ) : null}
        {detail.score !== null ? (
          <span dir="ltr" className="text-xs text-muted-foreground">
            {scoreState
              ? `score: ${detail.score.value} (${detail.score.scale})`
              : `score ${detail.score.value}/${detail.score.scale}`}
          </span>
        ) : scoreState ? (
          <span className="text-xs text-muted-foreground">
            score: none recorded
          </span>
        ) : null}
      </p>
      <p className="text-muted-foreground">
        Rationale: {RATIONALE_STATE_LABELS[detail.rationaleState]}
      </p>
      {needsRead && !requested ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setRequested(true)}
        >
          Show result and rationale
        </Button>
      ) : null}
      {requested && needsRead && read.isPending ? (
        <p className="text-muted-foreground">Reading the recorded text…</p>
      ) : requested && needsRead && read.isError ? (
        <p role="alert" className="text-destructive">
          The recorded text could not be read{describeDetailFailure(read.error)}
          .{" "}
          <button
            type="button"
            className="underline underline-offset-2"
            onClick={() => void read.refetch()}
          >
            Try again
          </button>
        </p>
      ) : (
        <>
          <Body label="Result" slot={slotOf("result", detail.result)} />
          {rationale !== null ? (
            <Body label="Rationale" slot={slotOf("rationale", rationale)} />
          ) : null}
        </>
      )}
      {children}
    </div>
  );
}

export function Body({
  label,
  slot,
}: {
  label: string;
  slot: ContentSlot | null;
}) {
  if (slot === null) {
    return null;
  }
  const withheld = describeWithheld(slot);
  const body = readInlineBody(slot);
  if (body === null && withheld === null) {
    return null;
  }
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      {body !== null ? (
        <p dir="auto" className="whitespace-pre-wrap break-words">
          {body.kind === "text"
            ? body.text
            : JSON.stringify(body.value, null, 2)}
        </p>
      ) : (
        <p className="text-muted-foreground">{withheld}</p>
      )}
    </div>
  );
}

export function describeDetailFailure(error: unknown): string {
  const failure = classifyRunReadFailure(error);
  switch (failure.kind) {
    case "not_found":
      return ": the run no longer answers for this event";
    case "denied":
      return ": your account is not authorized to read it";
    case "upstream":
      return failure.recovery === "retry"
        ? ": the preview target took too long to answer"
        : ": the preview target could not answer";
    case "error":
      return ` (${failure.code})`;
    case "transport":
      return ": the Editor Service could not be reached";
    case "unauthenticated":
    case "not_implemented":
      return "";
  }
}

/**
 * The criteria table. The Result column comes right after the criterion so it
 * is the second thing a reader reaches, never the last: at phone width the
 * table is wider than its box and scrolls INSIDE the box, and the rightmost
 * column is the one that scrolled out of sight (the clipped "Result" this
 * replaces). The wrapper scrolls on its own and says so while narrow.
 */
export function CriteriaTable({
  criteria,
  showSourceVersion = false,
}: {
  criteria: readonly CriterionEvent[];
  showSourceVersion?: boolean;
}) {
  const hintId = useId();
  return (
    <div className="space-y-1">
      <div
        className="overflow-x-auto rounded-md border border-border"
        // A scroll box has to be reachable by keyboard.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        role="group"
        aria-label="Criteria table"
        aria-describedby={hintId}
      >
        <table className="w-full text-sm">
          <caption className="sr-only">Criteria evaluated at this turn</caption>
          <thead>
            <tr className="text-start text-xs uppercase tracking-wide text-muted-foreground">
              <th scope="col" className="px-2 py-1.5 font-medium">
                Criterion
              </th>
              <th
                scope="col"
                className="px-2 py-1.5 font-medium whitespace-nowrap"
              >
                Result
              </th>
              <th scope="col" className="px-2 py-1.5 font-medium">
                Observed
              </th>
              <th scope="col" className="px-2 py-1.5 font-medium">
                Operator
              </th>
              <th scope="col" className="px-2 py-1.5 font-medium">
                Threshold
              </th>
              {showSourceVersion ? (
                <th scope="col" className="px-2 py-1.5 font-medium">
                  Source version
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {criteria.map((event) => (
              <tr
                key={event.eventRef}
                className="border-t border-border align-top"
              >
                <th
                  scope="row"
                  className="min-w-[11rem] px-2 py-1.5 text-start font-normal"
                >
                  <code dir="ltr" className="break-all font-mono text-xs">
                    {event.detail.criterionId}
                  </code>
                </th>
                <td className="px-2 py-1.5 whitespace-nowrap">
                  <Badge tone={event.detail.passed ? "success" : "warning"}>
                    {describeCriterionResult(event.detail.passed)}
                  </Badge>
                </td>
                <td dir="ltr" className="px-2 py-1.5">
                  {formatScalar(event.detail.observedValue)}
                </td>
                <td className="px-2 py-1.5">
                  {describeOperator(event.detail.operator)}
                </td>
                <td dir="ltr" className="px-2 py-1.5">
                  {formatScalar(event.detail.threshold)}
                </td>
                {showSourceVersion ? (
                  <td dir="ltr" className="px-2 py-1.5">
                    <code className="font-mono text-xs">
                      {event.detail.sourceVersion}
                    </code>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p id={hintId} className="text-xs text-muted-foreground">
        Scroll the table sideways to see every column.
      </p>
    </div>
  );
}

/** Names the sections beneath it for assistive tech ("Run A" / "Run B"), as the Summary does. */
export function SideScope({
  sideLabel,
  children,
}: {
  sideLabel: string | null;
  children: React.ReactNode;
}) {
  const sideId = useId();
  return (
    <SideLabelId.Provider value={sideLabel === null ? null : sideId}>
      {sideLabel !== null ? (
        <span id={sideId} className="sr-only">
          {sideLabel}
        </span>
      ) : null}
      {children}
    </SideLabelId.Provider>
  );
}

/**
 * The references an event cites (`inputRefs` / `evidenceRefs`), each resolved
 * against the run's own loaded events through `resolveLabRef` and stated as
 * what it is: a turn to jump to, a prompt call to open inline, a reference the
 * producer declared lost, or one that is not among the loaded events.
 * Only the opaque ref is ever used; the call card reads bodies on its own
 * explicit control.
 */
export function ResolvedRefs({
  label,
  refs,
  citingEventRef,
  field,
  model,
  projectId,
  previewRunRef,
  onSelectTurn,
}: {
  label: string;
  refs: readonly string[];
  citingEventRef: string;
  field: "inputRefs" | "evidenceRefs";
  model: LabRunModel;
  projectId: string;
  previewRunRef: string;
  onSelectTurn: (turn: number) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  if (refs.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">{label}: none recorded</p>
    );
  }
  return (
    <div className="space-y-1 text-sm">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <ul className="space-y-1">
        {refs.map((ref, index) => {
          const resolved = resolveLabRef(
            ref,
            { eventRef: citingEventRef, field, index },
            model,
          );
          if (resolved.kind === "declared_lost") {
            return (
              <li key={`${ref}-${index}`} className="text-muted-foreground">
                not captured ({resolved.reasonLabel})
              </li>
            );
          }
          if (resolved.kind === "unresolved") {
            return (
              <li key={`${ref}-${index}`} className="text-muted-foreground">
                not in this run&apos;s loaded events
              </li>
            );
          }
          if (resolved.kind === "prompt_call") {
            const call = model.prompts.find(
              (row) => row.eventRef === resolved.eventRef,
            )?.call;
            const isOpen = open === resolved.eventRef;
            return (
              <li key={`${ref}-${index}`} className="space-y-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  aria-expanded={isOpen}
                  disabled={call === undefined}
                  onClick={() => setOpen(isOpen ? null : resolved.eventRef)}
                >
                  <span dir="auto">Prompt call {resolved.label}</span>
                </Button>
                {isOpen && call !== undefined ? (
                  <PromptCallCard
                    call={call}
                    projectId={projectId}
                    previewRunRef={previewRunRef}
                    title="Prompt call"
                  />
                ) : null}
              </li>
            );
          }
          const turnNumber = resolved.turnNumber ?? labTurnForRef(model, ref);
          return (
            <li key={`${ref}-${index}`}>
              {turnNumber !== null ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onSelectTurn(turnNumber)}
                >
                  {resolved.kind === "turn_event" ? (
                    <>Turn {turnNumber}</>
                  ) : (
                    <span dir="auto">
                      {resolved.label} (turn {turnNumber})
                    </span>
                  )}
                </Button>
              ) : (
                <span dir="auto">{resolved.label} (not tied to a turn)</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
